import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  OPCOES_DESTINO_ITENS,
  opcaoDoDestino,
  tokenMascarado,
  validarCabecalhoToken,
  validarUrlDestino,
  valorDoDestino,
  type OpcaoDestinoItens,
} from "@/lib/destinoItens";
import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";

type CampoToken = "inovaid_token_integracao" | "destino_token_personalizado";

type Props = {
  impressaoConfig: ImpressaoConfigResponse;
  newImpressaoConfig: ImpressaoConfigResponse | null;
  setNewImpressaoConfig: React.Dispatch<
    React.SetStateAction<ImpressaoConfigResponse | null>
  >;
  isLoading: boolean;
};

/**
 * Destino dos itens impressos: Padrão (vazio), iNOVAONE, iNOVAID ou URL
 * personalizada. Os tokens chegam mascarados da API; o campo fica vazio e só
 * substitui o token salvo quando preenchido.
 */
export function DestinoItensImpressaoSection({
  impressaoConfig,
  newImpressaoConfig,
  setNewImpressaoConfig,
  isLoading,
}: Props) {
  const opcao = opcaoDoDestino(newImpressaoConfig);
  const descricao = OPCOES_DESTINO_ITENS.find((o) => o.valor === opcao)
    ?.descricao;

  const alterar = (patch: Partial<ImpressaoConfigResponse>) =>
    setNewImpressaoConfig((prev) => (prev ? { ...prev, ...patch } : prev));

  // Campo vazio = mantém o token salvo (volta o valor mascarado, que a API ignora)
  const alterarToken = (campo: CampoToken, valor: string) =>
    alterar({ [campo]: valor.trim() || impressaoConfig[campo] || "" });

  const valorDoCampoToken = (campo: CampoToken) => {
    const valor = newImpressaoConfig?.[campo] ?? "";
    return tokenMascarado(valor) ? "" : valor;
  };

  const ajudaDoToken = (campo: CampoToken, semToken: string) => {
    const atual = newImpressaoConfig?.[campo];
    if (tokenMascarado(atual)) {
      const final = (atual as string).replace(/^\*+/, "");
      return final
        ? `Token salvo, terminado em ${final}. Preencha somente para substituí-lo.`
        : "Token salvo. Preencha somente para substituí-lo.";
    }
    return semToken;
  };

  const url = newImpressaoConfig?.destino_url_personalizada ?? "";
  const erroUrl = url.trim() ? validarUrlDestino(url) : null;
  const cabecalho =
    newImpressaoConfig?.destino_cabecalho_token_personalizado ?? "";
  const erroCabecalho = validarCabecalhoToken(cabecalho);
  const tokenPersonalizadoSalvo = Boolean(
    newImpressaoConfig?.destino_token_personalizado,
  );

  return (
    <div className="col-span-2 md:col-span-4 grid gap-4 md:grid-cols-4 rounded-md border p-4">
      <div className="grid gap-2 md:col-span-2">
        <Label htmlFor="impressao-destino-itens">
          Destino dos itens impressos
        </Label>
        <Select
          disabled={isLoading || !newImpressaoConfig}
          value={opcao}
          onValueChange={(value) =>
            alterar({
              destino_itens: valorDoDestino(value as OpcaoDestinoItens),
            })
          }
        >
          <SelectTrigger id="impressao-destino-itens" className="w-full">
            <SelectValue placeholder="Padrão" />
          </SelectTrigger>
          <SelectContent sortable={false} searchable={false}>
            {OPCOES_DESTINO_ITENS.map((destino) => (
              <SelectItem key={destino.valor} value={destino.valor}>
                {destino.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{descricao}</p>
      </div>

      {opcao === "inovaid" && (
        <div className="grid gap-2 md:col-span-2">
          <Label htmlFor="impressao-inovaid-token">
            Token de integração do iNOVAID
          </Label>
          <Input
            disabled={isLoading}
            id="impressao-inovaid-token"
            type="password"
            autoComplete="new-password"
            placeholder={
              tokenMascarado(newImpressaoConfig?.inovaid_token_integracao)
                ? "••••••••"
                : ""
            }
            value={valorDoCampoToken("inovaid_token_integracao")}
            onChange={(e) =>
              alterarToken("inovaid_token_integracao", e.target.value)
            }
          />
          <p className="text-xs text-muted-foreground">
            {ajudaDoToken(
              "inovaid_token_integracao",
              "Gerado no iNOVAID, em Configurações > Integrações. Os itens são criados na conta dona do token.",
            )}
          </p>
        </div>
      )}

      {opcao === "personalizado" && (
        <>
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="impressao-destino-url">URL de destino</Label>
            <Input
              disabled={isLoading}
              id="impressao-destino-url"
              type="url"
              inputMode="url"
              placeholder="https://"
              value={url}
              aria-invalid={Boolean(erroUrl)}
              onChange={(e) =>
                alterar({ destino_url_personalizada: e.target.value })
              }
            />
            <p
              className={
                erroUrl
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {erroUrl ??
                "Endereço público que recebe os itens impressos por POST, em JSON. Endereços de rede interna não são aceitos."}
            </p>
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="impressao-destino-token">
              Token de acesso (opcional)
            </Label>
            <div className="flex gap-2">
              <Input
                disabled={isLoading}
                id="impressao-destino-token"
                type="password"
                autoComplete="new-password"
                placeholder={
                  tokenMascarado(newImpressaoConfig?.destino_token_personalizado)
                    ? "••••••••"
                    : ""
                }
                value={valorDoCampoToken("destino_token_personalizado")}
                onChange={(e) =>
                  alterarToken("destino_token_personalizado", e.target.value)
                }
              />
              {tokenPersonalizadoSalvo && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={isLoading}
                  onClick={() => alterar({ destino_token_personalizado: "" })}
                >
                  Remover token
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {ajudaDoToken(
                "destino_token_personalizado",
                "Enviado em cada requisição. Em branco, o envio é feito sem token.",
              )}
            </p>
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="impressao-destino-cabecalho">
              Cabeçalho do token (opcional)
            </Label>
            <Input
              disabled={isLoading}
              id="impressao-destino-cabecalho"
              placeholder="Authorization"
              value={cabecalho}
              aria-invalid={Boolean(erroCabecalho)}
              onChange={(e) =>
                alterar({
                  destino_cabecalho_token_personalizado: e.target.value,
                })
              }
            />
            <p
              className={
                erroCabecalho
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {erroCabecalho ??
                "Em branco, o token é enviado como Authorization: Bearer <token>."}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

